/**
 * Mock GitHub Enrichment Data
 *
 * Provides realistic portfolio intelligence data based on known projects
 * until GitHub API token is configured.
 *
 * To use real GitHub data: Set GITHUB_TOKEN environment variable
 */

import type { GitHubEnrichmentData } from './github-enrichment.service'

export function getMockEnrichmentData(): GitHubEnrichmentData {
  return {
    username: 'AUGE2U',

    profile: {
      name: 'Agustin Musi',
      bio: 'Full-stack engineer specializing in modern web development, cloud-native architecture, and cost-efficient infrastructure',
      location: 'Switzerland',
      company: 'HabitusNet',
      blog: 'https://agustinmusi.com',
      email: 'musia@habitusnet.ch',
      hireable: true,
      publicRepos: 12,
      publicGists: 3,
      followers: 45,
      following: 28,
      createdAt: '2016-03-15T10:00:00Z',
      updatedAt: '2025-11-11T12:00:00Z'
    },

    repositories: [
      {
        id: 1,
        name: 'agustin-musi-portfolio',
        fullName: 'AUGE2U/agustin-musi-portfolio',
        description: 'Professional portfolio v2 with 4-layer architecture, React 19, Neon PostgreSQL, and Cloudflare Workers',
        url: 'https://github.com/AUGE2U/agustin-musi-portfolio',
        homepage: 'https://agustinmusi.com',

        languages: {
          'TypeScript': { bytes: 450000, percentage: 75 },
          'JavaScript': { bytes: 80000, percentage: 13.3 },
          'CSS': { bytes: 60000, percentage: 10 },
          'HTML': { bytes: 10000, percentage: 1.7 }
        },
        primaryLanguage: 'TypeScript',
        topics: ['react', 'typescript', 'vite', 'cloudflare-workers', 'neon', 'prisma', 'design-system', 'portfolio'],
        techStack: ['React', 'TypeScript', 'Vite', 'Cloudflare', 'Neon', 'Prisma', 'Stack Auth'],

        linesOfCode: 15000,
        codeComplexity: 'complex',
        architecturePattern: 'Serverless / Edge',

        stars: 8,
        forks: 2,
        watchers: 8,
        openIssues: 3,
        hasWiki: true,
        hasProjects: true,

        lastCommit: '2025-11-11T12:00:00Z',
        commitFrequency: 'active',
        contributors: 1,

        ownershipRole: 'owner',
        pullRequests: 45,
        issuesResolved: 12,

        license: 'MIT',
        visibility: 'public',
        isTemplate: false,
        isFork: false,

        infrastructureType: 'serverless',
        estimatedMonthlyCost: 5,
        costOptimization: [
          'Serverless architecture reduces idle costs',
          'Cloudflare CDN reduces bandwidth and latency costs',
          'Autoscaling database (0.25-2 CU) reduces costs',
          'Migration from Supabase ($30/mo) to Neon ($0/mo) = 83% savings'
        ],

        innovationScore: 92,
        marketRelevance: 'Highly Relevant',
        uniqueFeatures: [
          'React 19 + Cloudflare Workers edge deployment',
          'Serverless PostgreSQL with Prisma ORM',
          '4-layer semantic architecture (Layout/Design/View/Role)',
          'Cultural design token system (Japanese Zen, Scandinavian)',
          'Progressive disclosure with gated content',
          'Perspective-oriented portfolio intelligence'
        ]
      },
      {
        id: 2,
        name: 'design-token-system',
        fullName: 'AUGE2U/design-token-system',
        description: 'Cultural design token library with Japanese Zen, Scandinavian Hygge, and Mediterranean aesthetics',
        url: 'https://github.com/AUGE2U/design-token-system',
        homepage: '',

        languages: {
          'JavaScript': { bytes: 120000, percentage: 60 },
          'CSS': { bytes: 60000, percentage: 30 },
          'TypeScript': { bytes: 20000, percentage: 10 }
        },
        primaryLanguage: 'JavaScript',
        topics: ['design-tokens', 'design-system', 'css-variables', 'theming', 'cultural-design'],
        techStack: ['JavaScript', 'CSS', 'Style Dictionary', 'Design Tokens'],

        linesOfCode: 5000,
        codeComplexity: 'moderate',
        architecturePattern: 'Component-based SPA',

        stars: 15,
        forks: 4,
        watchers: 15,
        openIssues: 2,
        hasWiki: false,
        hasProjects: false,

        lastCommit: '2025-11-05T08:00:00Z',
        commitFrequency: 'moderate',
        contributors: 1,

        ownershipRole: 'owner',
        pullRequests: 18,
        issuesResolved: 8,

        license: 'MIT',
        visibility: 'public',
        isTemplate: true,
        isFork: false,

        infrastructureType: 'static',
        estimatedMonthlyCost: 0,
        costOptimization: [
          'Zero compute costs for static hosting',
          'CDN caching reduces bandwidth costs'
        ],

        innovationScore: 85,
        marketRelevance: 'Highly Relevant',
        uniqueFeatures: [
          'Cultural aesthetics (Wabi-Sabi, Hygge, Mediterranean)',
          'Semantic token organization',
          'Multi-theme support with CSS variables'
        ]
      },
      {
        id: 3,
        name: 'react-19-migration-toolkit',
        fullName: 'AUGE2U/react-19-migration-toolkit',
        description: 'Automated migration tools and best practices for upgrading React 18 to React 19',
        url: 'https://github.com/AUGE2U/react-19-migration-toolkit',
        homepage: '',

        languages: {
          'TypeScript': { bytes: 180000, percentage: 85 },
          'JavaScript': { bytes: 30000, percentage: 14 },
          'Markdown': { bytes: 2000, percentage: 1 }
        },
        primaryLanguage: 'TypeScript',
        topics: ['react', 'react-19', 'migration', 'typescript', 'automation'],
        techStack: ['TypeScript', 'React', 'Node.js', 'ESLint'],

        linesOfCode: 8000,
        codeComplexity: 'complex',
        architecturePattern: 'CLI Tool',

        stars: 22,
        forks: 6,
        watchers: 22,
        openIssues: 1,
        hasWiki: true,
        hasProjects: false,

        lastCommit: '2025-10-25T14:00:00Z',
        commitFrequency: 'maintained',
        contributors: 1,

        ownershipRole: 'owner',
        pullRequests: 12,
        issuesResolved: 5,

        license: 'MIT',
        visibility: 'public',
        isTemplate: false,
        isFork: false,

        infrastructureType: 'static',
        estimatedMonthlyCost: 0,
        costOptimization: [
          'CLI tool with zero hosting costs',
          'NPM package distribution'
        ],

        innovationScore: 88,
        marketRelevance: 'Highly Relevant',
        uniqueFeatures: [
          'Automated dependency detection',
          'Breaking change analysis',
          'Zero-downtime migration patterns'
        ]
      }
    ],

    organizations: [
      {
        login: 'habitusnet',
        name: 'HabitusNet',
        description: 'Digital transformation consulting and modern web development',
        url: 'https://github.com/habitusnet',
        publicRepos: 8,
        role: 'owner'
      }
    ],

    technicalProfile: {
      primaryLanguages: ['TypeScript', 'JavaScript', 'CSS', 'HTML', 'SQL'],
      frameworks: ['React', 'Vite', 'Next.js', 'Prisma'],
      databaseSystems: ['PostgreSQL', 'Neon', 'Supabase'],
      cloudPlatforms: ['Cloudflare', 'Vercel', 'AWS'],
      architecturePatterns: ['Serverless / Edge', 'Component-based SPA', 'Microservices', 'CLI Tool'],
      totalLinesOfCode: 28000,
      averageCodeComplexity: 'complex',
      experienceYears: 8
    },

    leadershipProfile: {
      projectsOwned: 12,
      projectsMaintained: 3,
      contributedProjects: 8,
      teamCollaborations: 5,
      pullRequestsReviewed: 75,
      mentorshipIndicators: [
        'Active code reviewer',
        'Open source maintainer',
        'Documentation contributor',
        'Technical blog author'
      ]
    },

    businessImpact: {
      totalStars: 45,
      totalForks: 12,
      communityReach: 69,
      openSourceContributions: 12,
      costSavingsAchieved: 300, // $300/year
      roiMetrics: [
        {
          description: 'Infrastructure cost optimization',
          value: 83,
          unit: '% reduction'
        },
        {
          description: 'Community reach multiplier',
          value: 5,
          unit: 'x per project'
        },
        {
          description: 'Migration success rate',
          value: 100,
          unit: '% zero-downtime'
        }
      ]
    },

    perspectives: {
      recruiter: {
        summary: '8+ years of experience building scalable applications with TypeScript, React, and modern cloud platforms. Proven track record of 12 successful projects and 5 team collaborations.',

        keyStrengths: [
          'Full-stack development with modern tech stacks (React 19, TypeScript, Prisma)',
          'Cloud-native architecture and serverless deployment (Cloudflare Workers, Neon)',
          'Cost optimization and infrastructure efficiency (83% cost reduction achieved)',
          'Open source contribution and community engagement (45+ stars)',
          'Technical leadership and mentorship (75+ PRs reviewed)'
        ],

        teamFit: [
          'Collaborative code reviewer and mentor (75+ PRs reviewed)',
          'Strong documentation and knowledge sharing (Wiki maintainer)',
          'Proactive problem solver (100% zero-downtime migrations)',
          'Adapts to new technologies quickly (Early React 19 adopter)'
        ],

        culturalValues: [
          'Open source contribution (12 public repositories)',
          'Clean code and best practices (TypeScript strict mode)',
          'Continuous learning (Latest tech: React 19, Vite 6, Prisma)',
          'Cost-conscious engineering (83% infrastructure savings)'
        ],

        impactMetrics: [
          {
            label: 'Projects Led',
            value: '12',
            context: 'Full ownership and delivery'
          },
          {
            label: 'Community Reach',
            value: '45+ stars',
            context: 'Open source impact'
          },
          {
            label: 'Cost Optimization',
            value: '83%',
            context: 'Infrastructure savings achieved'
          },
          {
            label: 'Team Collaboration',
            value: '5 teams',
            context: 'Cross-functional experience'
          }
        ],

        standoutProjects: [
          {
            name: 'agustin-musi-portfolio',
            impact: '8 stars, 2 forks - Professional portfolio v2 with 4-layer architecture',
            skills: ['React', 'TypeScript', 'Cloudflare', 'Neon', 'Prisma']
          },
          {
            name: 'react-19-migration-toolkit',
            impact: '22 stars, 6 forks - Automated migration tools for React 19',
            skills: ['TypeScript', 'React', 'Node.js', 'ESLint']
          },
          {
            name: 'design-token-system',
            impact: '15 stars, 4 forks - Cultural design token library',
            skills: ['JavaScript', 'CSS', 'Style Dictionary']
          }
        ]
      },

      technical: {
        architectureExpertise: [
          'Serverless / Edge Computing',
          'Component-based SPA',
          'Microservices',
          'CLI Tools & Automation'
        ],

        codeQualityMetrics: [
          {
            metric: 'Code Complexity',
            score: 85,
            evidence: 'Average complex across 12 projects with 28,000 LOC'
          },
          {
            metric: 'Modern Stack Adoption',
            score: 95,
            evidence: 'Using latest versions: React 19, Vite 6, TypeScript, Prisma'
          },
          {
            metric: 'Open Source Quality',
            score: 90,
            evidence: '45 community stars, 12 forks'
          },
          {
            metric: 'Test Coverage',
            score: 80,
            evidence: 'Comprehensive E2E testing with Playwright'
          }
        ],

        technicalDecisions: [
          {
            decision: 'agustin-musi-portfolio: serverless architecture',
            rationale: 'Cloudflare Workers for edge deployment, reducing latency and costs',
            outcome: '$5/month hosting cost vs $30/month traditional (83% savings)'
          },
          {
            decision: 'Database migration: Supabase → Neon PostgreSQL',
            rationale: 'Autoscaling (0.25-2 CU) reduces idle costs, better Prisma integration',
            outcome: '$0/month database cost (Free plan) vs $25/month Supabase Pro'
          },
          {
            decision: 'React 18 → 19 migration with zero downtime',
            rationale: 'Phased migration strategy with comprehensive testing',
            outcome: '100% success rate, no production incidents'
          }
        ],

        contributionPatterns: [
          {
            type: 'Full-stack development',
            frequency: 'Daily',
            complexity: 'High'
          },
          {
            type: 'Architecture & design',
            frequency: 'Weekly',
            complexity: 'Expert'
          },
          {
            type: 'Code reviews & mentoring',
            frequency: 'Regular',
            complexity: 'Moderate-High'
          },
          {
            type: 'Documentation & knowledge sharing',
            frequency: 'Weekly',
            complexity: 'Moderate'
          }
        ],

        standoutImplementations: [
          {
            name: 'agustin-musi-portfolio',
            technicalChallenge: '4-layer semantic architecture eliminating "theme" confusion',
            solution: 'Orthogonal separation: LayoutTheme, DesignTokenSet, ViewerProfile, RolePersona',
            technologies: ['React', 'TypeScript', 'Vite', 'Cloudflare Workers', 'Neon', 'Prisma']
          },
          {
            name: 'design-token-system',
            technicalChallenge: 'Multi-cultural design system with semantic tokens',
            solution: 'Style Dictionary pipeline with cultural aesthetics (Wabi-Sabi, Hygge)',
            technologies: ['JavaScript', 'CSS Variables', 'Style Dictionary']
          },
          {
            name: 'react-19-migration-toolkit',
            technicalChallenge: 'Automated detection of React 19 breaking changes',
            solution: 'AST analysis with ESLint rules for safe migration paths',
            technologies: ['TypeScript', 'ESLint', 'Node.js']
          }
        ]
      },

      executive: {
        strategicValue: '$300/year in infrastructure savings with proven ability to deliver scalable solutions that reduce operational overhead while maintaining high performance and reliability.',

        costOptimizations: [
          {
            initiative: 'Database Migration: Supabase → Neon PostgreSQL',
            savingsAmount: 300,
            savingsPercentage: 83,
            timeframe: 'Annual'
          },
          {
            initiative: 'Serverless Architecture: Cloudflare Workers',
            savingsAmount: 180,
            savingsPercentage: 75,
            timeframe: 'Annual'
          },
          {
            initiative: 'Static Hosting: Design Token System',
            savingsAmount: 60,
            savingsPercentage: 100,
            timeframe: 'Annual'
          }
        ],

        scalabilityAchievements: [
          'Serverless architecture enabling 0-100k users with no infrastructure changes',
          'Autoscaling database (0.25-2 CU) adapting to load automatically',
          'Edge computing deployment for global <100ms latency',
          'Zero-downtime deployments with instant rollback capability',
          'Multi-tenant architecture supporting unlimited clients'
        ],

        riskMitigations: [
          'Multi-cloud strategy reducing vendor lock-in (Cloudflare, Neon, Vercel)',
          'Comprehensive test coverage ensuring quality (86% success rate)',
          'Infrastructure as Code for reproducibility (Prisma migrations)',
          'Security-first approach with RLS policies and gated content',
          'Point-in-time restore (6-hour PITR on Free plan)'
        ],

        businessOutcomes: [
          {
            outcome: 'Cost Reduction',
            impact: '83% infrastructure cost savings ($300/year)',
            timeline: 'Achieved in Q4 2024'
          },
          {
            outcome: 'Platform Modernization',
            impact: 'React 18 → 19 migration with zero downtime',
            timeline: 'Completed Q4 2024'
          },
          {
            outcome: 'Architecture Consolidation',
            impact: '4-layer semantic model eliminating technical debt',
            timeline: 'Ongoing - 70% complete'
          },
          {
            outcome: 'Developer Experience',
            impact: 'Prisma ORM reducing query complexity by 60%',
            timeline: 'Delivered Q4 2024'
          }
        ]
      },

      public: {
        professionalSummary: 'Full-stack engineer specializing in modern web development, cloud-native architecture, and cost-efficient infrastructure. Passionate about open source, clean code, and building scalable applications with React, TypeScript, and serverless technologies.',

        notableAchievements: [
          'Built and deployed production systems serving thousands of users',
          'Reduced infrastructure costs by 83% through strategic architecture decisions',
          'Contributed 12 open source projects with 45+ community stars',
          'Migrated critical systems to latest technologies (React 19) with zero downtime',
          'Designed 4-layer architecture pattern eliminating semantic confusion',
          'Created cultural design token system with Japanese and Scandinavian aesthetics'
        ],

        expertiseAreas: [
          'React & TypeScript',
          'Serverless Architecture',
          'PostgreSQL & Prisma',
          'Cloudflare Workers',
          'Cost Optimization',
          'Design Systems',
          'Cloud-Native Development',
          'Zero-Downtime Migrations'
        ],

        openSourceHighlights: [
          {
            project: 'react-19-migration-toolkit',
            description: 'Automated migration tools and best practices for upgrading React 18 to React 19',
            stars: 22
          },
          {
            project: 'design-token-system',
            description: 'Cultural design token library with Japanese Zen, Scandinavian Hygge, and Mediterranean aesthetics',
            stars: 15
          },
          {
            project: 'agustin-musi-portfolio',
            description: 'Professional portfolio v2 with 4-layer architecture, React 19, Neon PostgreSQL, and Cloudflare Workers',
            stars: 8
          }
        ]
      }
    }
  }
}
